import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { IonInput, ModalController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrderType } from 'src/smoothr-web-app-core/enums/OrderType';
import { calculateGeoDistance, MapsUtils } from '../../utils/maps-leaflet';
import { MapsUtils as MapUtils2 } from 'src/smoothr-web-app-core/utils/maps-utils';

import { Api } from '../../../smoothr-web-app-core/api/api';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import Address from '../../../smoothr-web-app-core/models/Address';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { TimeUtils } from '../../../smoothr-web-app-core/utils/time-utils';
import { coverFlow, sleep, venueAcceptsOrders } from '../../../smoothr-web-app-core/utils/utils';

import { OrderUtils } from 'src/smoothr-web-app-core/utils/order-utils';
import { ValidationUtils } from 'src/smoothr-web-app-core/utils/validation-utils';
import { MapPage } from 'src/app/pages/map/map.page';
import { NavigationService } from 'src/app/services/navigation.service';
import { CheckDeliveryRadiusModalComponent } from 'src/app/components/check-delivery-radius-modal/check-delivery-radius-modal.component';
import { AppComponent } from 'src/app/app.component';
import { EnterFoodspotCodeModalComponent } from 'src/app/components/enter-foodspot-code-modal/enter-foodspot-code-modal.component';
import { FormControl } from '@angular/forms';
import { SearchCity } from 'src/smoothr-web-app-core/models/SearchCity';
import PromoCode from 'src/smoothr-web-app-core/models/PromoCode';
import { ErrorPromcodeDialogComponent } from 'src/app/components/error-promcode-dialog/error-promcode-dialog.component';
import { PromoCodeType } from 'src/smoothr-web-app-core/models/PromoCodeType';
import { AnalyticsService } from 'src/smoothr-web-app-core/services/analytics/analytics.service';

import * as leaflet from 'leaflet';
import '@maplibre/maplibre-gl-leaflet';

@Component({
	selector: 'app-map-venue',
	templateUrl: './map-venue.component.html',
	styleUrls: ['./map-venue.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapVenueComponent extends RepositoryDirective implements OnInit, OnDestroy, AfterViewInit {
	@Input() showSearchInput = false;
	@Input() showVenueList = true;
	@Input() venueToFirstPositionShortId = '';
	@ViewChild('mapContainer', { static: true })
	mapElement: ElementRef;
	@ViewChild('swiper') slides: ElementRef | undefined;
	map: leaflet.Map;
	@ViewChild(IonInput, { static: true })
	inputField: IonInput;
	@ViewChild('venueList', { static: false }) venueList;
	public preorderTypes = [PreorderType.INSIDE, PreorderType.TAKE_AWAY, PreorderType.DELIVERY, PreorderType.FOOD_SPOT].filter(it =>
		environment.deactivateDelivery ? it !== PreorderType.DELIVERY && environment.deactivateDelivery : true
	);
	mapOptions: leaflet.MapOptions = {
		center: { lat: 51.1657, lng: 10.4515 }, // Center of Germany
		zoom: 6,
		maxZoom: 15,
		minZoom: 5,
		layers: [
			leaflet.maplibreGL({
				style: 'https://tiles.openfreemap.org/styles/liberty',
			}),
		],
		zoomControl: false,
	};
	clusterer: leaflet.MarkerClusterGroup;
	loading = false;
	loadingAddress = false;
	predictions: any[] = [];
	showHint = false;
	searchTerm: string;
	allVenues: Venue[] = [];
	localVenues: Venue[] = [];
	selectedVenueForDelivery: Venue;
	showMap = true;
	slidesOpts = {
		slidesPerView: 1,
		coverflowEffect: {
			rotate: 0,
			stretch: 15,
			depth: 10,
			modifier: 2,
		},
		on: coverFlow,
	};
	selectedVenue: Venue;
	MapsUtils = MapsUtils;
	private _showPredictions = false;
	selectedType: PreorderType | null = null;
	showErrorSelectOrderType: boolean = false;
	searchCityForm: FormControl = new FormControl('');
	private onDestroy$ = new Subject<void>();
	public cityPrediction: SearchCity[] = [];
	public promoCodeId: string = '';
	public readableId: string | null | undefined = undefined;

	constructor(
		private snackbarCtrl: MatSnackBar,
		public repository: RepositoryService,
		private translate: TranslateService,
		private router: Router,
		private cdr: ChangeDetectorRef,
		private modalCtrl: ModalController,
		private route: ActivatedRoute,
		private navService: NavigationService,
		private popover: PopoverController,
		private analytics: AnalyticsService
	) {
		super(repository);
	}

	get relevantVenues(): Venue[] {
		switch (this.selectedType) {
			case PreorderType.DELIVERY:
				if (this.address && this.localVenues.length > 0) {
					return this.localVenues;
				} else {
					return this.allVenues.filter(it => venueAcceptsOrders(it, PreorderType.DELIVERY));
				}
			default:
				return this.allVenues.filter(it => venueAcceptsOrders(it, this.selectedType));
		}
	}
	trackByVenue(index: number, venue: Venue) {
		return venue._id;
	}

	static navigate(router: Router) {
		return router.navigateByUrl(MapPage.url);
	}
	ngAfterViewInit(): void {
		// Initialize Leaflet map
		this.map = leaflet.map(this.mapElement.nativeElement, this.mapOptions);
		this.map.attributionControl.setPrefix('');
		setTimeout(function () {
			window.dispatchEvent(new Event('resize'));
		}, 1000);

		// If address is already available (e.g., after page refresh), load venues on map
		if (this.address && this.localVenues?.length > 0) {
			this.setupMap(null);
		}

		this.setupVenueListKeyboardNavigation();
	}
	ngOnInit() {
		super.ngOnInit();
		this.searchCityForm.valueChanges.pipe(debounceTime(600), takeUntil(this.onDestroy$)).subscribe(v => {
			if (!v) {
				this.resetMap(true);
			} else {
				this.loadCity(v);
			}
		});

		new Promise<void>(async resolve => {
			await this.fillInPlace(null);
			await this.initAutocomplete();
			resolve();
		});
		this.route.queryParams.subscribe(v => {
			if (v?.preorderType) {
				this.selectedType = v.preorderType;
				if (this.selectedType === PreorderType.DELIVERY) {
					this.showDialogForAddress();
				}
			}
			if (v?.promoCodeId) {
				this.promoCodeId = v.promoCodeId;
			}
		});

		this.route.paramMap.subscribe(params => {
			const readableId = params.get(MapPage.readableId.replace(':', ''));
			if (readableId) {
				this.readableId = 'dnd_' + readableId;
			} else {
				this.readableId = null;
			}
			this.resetMap();
		});
	}
	async loadCity(search: string) {
		try {
			this.cityPrediction = await Api.loadCitiesFromService(search);
		} catch (e) {}
	}
	ngOnDestroy(): void {
		this.onDestroy$.next();
	}
	async resetMap(ignoreExistingOfVenue: boolean = false) {
		if ((this.allVenues.length === 0 || ignoreExistingOfVenue) && this.readableId !== undefined) {
			try {
				this.allVenues = (
					this.readableId
						? [(await Api.getLazyVenue(this.readableId, true)).data]
						: (await Api.getAllVenues(environment.customerGroup)).data
				)
					.map(ven => {
						try {
							ven.openingHours = TimeUtils.sanitizeHours(ven.openingHours, ven.specialOpeningHours ?? []);
							// ven.openingHours = TimeUtils.checkSpecialHours(resultMapping, ven?.specialOpeningHours ?? []);
							ven.deliveryHours = TimeUtils.sanitizeHours(ven.deliveryHours);
						} catch (e) {
							console.error({
								message: 'Error while sanitizing hours ' + e,
								venue: ven.name + ' ' + ven.readableId,
								openingHours: ven.openingHours,
								deliveryHours: ven.deliveryHours,
							});
						}
						return ven;
					})
					.filter(
						ven =>
							ven.isPublished &&
							(venueAcceptsOrders(ven, PreorderType.DELIVERY) ||
								venueAcceptsOrders(ven, PreorderType.TAKE_AWAY) ||
								venueAcceptsOrders(ven, PreorderType.INSIDE))
					)
					.sort((a, b) => {
						const nameA = a?.name?.replace('Dean & David', '')?.trim();
						const nameB = b?.name?.replace('Dean & David', '')?.trim();
						return nameA?.localeCompare(nameB);
					});
				if (this.address) {
					this.allVenues = this.allVenues.sort((a, b) => {
						return (
							calculateGeoDistance(this.address.lat, this.address.lng, a.location.coordinates[1], a.location.coordinates[0]) -
							calculateGeoDistance(this.address.lat, this.address.lng, b.location.coordinates[1], b.location.coordinates[0])
						);
					});
				}
				this.allVenues = this.allVenues.filter(it => it.readableId != 'dispatch_test_dnd_frei_komtur');
			} catch (e) {}
		}
		await this.setupMap(null);
		this.cdr.markForCheck();
	}

	async setupMap(selectedVenue: Venue) {
		if (this.loading) {
			return;
		}
		this.selectedVenue = selectedVenue ?? this.relevantVenues[0];
		this.loading = true;
		this.cdr.markForCheck();
		while (!this.map) {
			await sleep(50);
		}
		this.clusterer = MapsUtils.addVenuesToMap(this.clusterer, selectedVenue, this.relevantVenues, this.map, venue =>
			this.setupMap(venue)
		);
		if (selectedVenue && selectedVenue.location && selectedVenue.location.coordinates) {
			const selectedIndex = this.relevantVenues.findIndex(ven => ven._id === selectedVenue._id);
			// wait until slides rendered
			this.scrollToVenue(selectedIndex ?? 0);

			const swiper = await this.getSwiperInstance();
			if (swiper && selectedIndex >= 0) {
				await swiper.slideTo(selectedIndex);
			}
		}
		this.loading = false;
		this.cdr.markForCheck();
	}

	hidePredictions() {
		setTimeout(() => {
			this._showPredictions = false;
		}, 200);
	}

	showPredictions() {
		this._showPredictions = true;
	}

	async executeSearch() {
		this.loading = true;
		try {
			const result = await MapUtils2.executeSearch(this.inputField);
			await this.fillInPlace(result);
		} catch (e) {}
		this.loading = false;
	}

	resetSearch() {
		this.searchTerm = '';
		this.predictions = [];
		this.selectedVenueForDelivery = null;
		this.selectedVenue = null;
		if (this.address) {
			this.repository.address.emit(null);
		}
		this.showHint = false;
		this.cdr.markForCheck();
	}

	async fillInPlace(address: Address) {
		if (!address) {
			return;
		}
		this.loading = true;
		try {
			this.searchTerm = MapsUtils.checkAddress(address);
			this.loading = false;
			if (this.address !== address) {
				this.repository.address.emit(address);
			}
			await this.onSlideChange();
		} catch (e) {
			console.error(e);
			this.snackbarCtrl.open(this.translate.instant(e), null, {
				duration: 2000,
			});
		}
		this.loading = false;
	}

	async loadVenues(address: Address) {
		if (this.loading) {
			return;
		}
		this.loading = true;

		this.cdr.markForCheck();
		this.localVenues = [];
		try {
			this.localVenues = (await this.repository.getVenuesByAddress(environment.customerGroup, address)).filter(it =>
				venueAcceptsOrders(it, PreorderType.DELIVERY)
			);
			this.searchTerm = MapsUtils.addressToString(address);
			if (this.localVenues.length === 0) {
				this.snackbarCtrl.open(
					this.translate.instant('map_page.no_venues_in_address', {
						title: this.searchTerm,
					}),
					null,
					{
						duration: 2000,
					}
				);
				this.loading = false;
				await this.resetMap();

				this.cdr.markForCheck();
				return;
			}
			if (this.selectedVenueForDelivery) {
				const deliveryVenues = this.localVenues.filter(ven => venueAcceptsOrders(ven, PreorderType.DELIVERY));
				const selectedDeliveryVenue = deliveryVenues.find(ven => ven._id === this.selectedVenueForDelivery._id);
				if (selectedDeliveryVenue) {
					await this.selectVenue(selectedDeliveryVenue, PreorderType.DELIVERY);
				} else {
					// const choice = await DeliveryNotAvailableModalComponent.show(this.modalCtrl);
					// switch (choice) {
					// 	case DeliveryNotAvailableAction.DECLINE:
					// 		this.localVenues = deliveryVenues;
					// 		if (deliveryVenues.length === 0) {
					// 			this.repository.address.emit(null);
					// 		} else {
					// 			this.showHint = true;
					// 		}
					// 		await this.setupMap(null);
					// 		break;
					// 	case DeliveryNotAvailableAction.TAKE_AWAY:
					// 		await this.selectVenue(this.selectedVenueForDelivery, PreorderType.TAKE_AWAY);
					// 		break;
					// }
				}
				this.selectedVenueForDelivery = null;
				return;
			}
			this.selectedVenue = null;
			this.loading = false;
			await this.setupMap(this.selectedVenue);
		} catch (e) {
			console.error(e);
		}
		this.loading = false;

		this.cdr.markForCheck();
	}

	async onSlideChange() {
		const swiper = await this.getSwiperInstance();
		let index = swiper?.activeIndex ?? 0;
		if (index >= this.relevantVenues.length) {
			index = 0;
		}
		await this.setupMap(this.relevantVenues[index]);
	}

	private async getSwiperInstance(retries = 20, delayMs = 50): Promise<any | null> {
		for (let attempt = 0; attempt < retries; attempt++) {
			const swiper = this.slides?.nativeElement?.swiper;
			if (swiper?.params) {
				return swiper;
			}
			await sleep(delayMs);
		}
		return null;
	}

	async selectVenue(venue: Venue, preorderType: PreorderType, attempt: number = 0, prevError: any = null) {
		this.showErrorSelectOrderType = false;
		if (!preorderType) {
			this.showErrorSelectOrderType = true;
			return;
		}
		if (!venueAcceptsOrders(venue, preorderType)) {
			return;
		}
		preorderType = this.selectedType;
		if (preorderType === PreorderType.DELIVERY && !this.address) {
			// this.snackbarCtrl.open(this.translate.instant('map_page.enter_delivery_address'), null, {
			// 	duration: 2000,
			// });
			await this.showDialogForAddress();
			this.selectedVenueForDelivery = venue;
			this.showHint = true;
			return;
		}
		let promo: PromoCode;

		if (this.promoCodeId) {
			try {
				promo = (await Api.getPromoCodeById(this.promoCodeId)).data;
				if (promo?.venues && promo.venues.length > 0) {
					if (!promo.venues.find(it => it === venue._id || it === venue?.masterId)) {
						const result = await ErrorPromcodeDialogComponent.show(this.modalCtrl, true);
						if (!result) {
							return;
						}
						promo = null;
					}
				}
			} catch (e) {}
		}
		if (
			preorderType === PreorderType.DELIVERY &&
			venue.distance > venue.deliveryRadius &&
			venue.deliveryByRadius &&
			!venue.isPostalDelivery
		) {
			this.snackbarCtrl.open(this.translate.instant('map_page.venue_does_not_delivery_to_address'), null, {
				duration: 2000,
			});
			this.showHint = true;

			return;
		}
		if (attempt > 5) {
			this.loading = false;
			this.snackbarCtrl.open(prevError, null, {
				duration: 2000,
			});
			return;
		}
		this.loading = true;
		if (preorderType === PreorderType.PARK_COLLECT) {
			// preorderType = await ParkCollectSheetComponent.show(this.modalCtrl, venue);
		}
		if (preorderType == PreorderType.DELIVERY && this.address && venue) {
			if (!(await this.checkAddressDelivery(venue, this.address))) {
				this.snackbarCtrl.open(this.translate.instant('map_page.venue_does_not_delivery_to_address'), null, {
					duration: 2000,
				});
				this.loading = false;
				this.repository.address.next(null);
				return;
			}
		}
		try {
			await this.repository.getVenue(venue._id);
			this.analytics.setVenue(venue.name);
			this.analytics.setCountry(venue.country ?? '');
			this.analytics.venueSelected(venue.name, null, preorderType);
			this.analytics.startFlow('venue_to_menu');
			this.repository.createOrder(venue, this.address, OrderType.PREORDER, preorderType);
			if (promo) {
				await sleep(300);
				if (promo.type === PromoCodeType.FREE_ARTICLE) {
					this.repository.promoCodeFree$.next(promo);
				} else {
					this.repository.promoCodeFree$.next(null);
					this.repository.onOrderChange(OrderUtils.applyPromo(this.translate, this.venue, this.order, promo, this.analytics));
				}
			}
			if (AppComponent.isNativeApp) {
				await this.navService.navigateSelectedCategory();
			} else {
				if (preorderType === PreorderType.FOOD_SPOT) {
					await this.navService.foodSpot();
				} else {
					await this.navService.menu();
				}
			} // this.router.navigateByUrl('/menu', {
			// 	replaceUrl: true,
			// });
			this.loading = false;
		} catch (e) {
			await this.selectVenue(venue, preorderType, attempt + 1, e);
		}
	}

	async initAutocomplete() {
		try {
			await MapUtils2.initAutocompleteCity(
				this.inputField,
				predictions => {
					this.predictions = predictions as any[];
					this.cdr.markForCheck();
				}
				// loading => (this.loadingAddress = loading)
			);
		} catch (e) {}

		this.loading = false;
	}

	async loadPlace(pred: SearchCity) {
		if (this.loading) {
			return;
		}
		this.loading = true;
		if (pred) {
			try {
				const result = await Api.getListOfVenues(pred.name);
				if (result.data) {
					if (result.data.length > 0) {
						const validatedVenues = result.data
							.map(ven => {
								try {
									ven.openingHours = TimeUtils.sanitizeHours(ven.openingHours, ven.specialOpeningHours ?? []);
									ven.deliveryHours = TimeUtils.sanitizeHours(ven.deliveryHours);
								} catch (e) {
									console.error({
										message: 'Error while sanitizing hours ' + e,
										venue: ven.name + ' ' + ven.readableId,
										openingHours: ven.openingHours,
										deliveryHours: ven.deliveryHours,
									});
								}
								return ven;
							})
							.filter(
								ven =>
									ven.isPublished &&
									(venueAcceptsOrders(ven, PreorderType.DELIVERY) || venueAcceptsOrders(ven, PreorderType.TAKE_AWAY))
							);
						if (validatedVenues.length > 0) {
							this.loading = false;
							this.allVenues = validatedVenues;
							this.cityPrediction = [];
							await this.setupMap(null);
						} else {
							this.loading = false;
							this.snackbarCtrl.open('Sorry, shops in this city were not found ...', null, {
								duration: 4000,
							});
						}
					} else {
						this.loading = false;
						this.snackbarCtrl.open('Sorry, shops in this city were not found ...', null, {
							duration: 4000,
						});
					}
				}
			} catch (e) {
				this.snackbarCtrl.open('Sorry, shops were not found...', null, {
					duration: 4000,
				});
				this.loading = false;
			}
		}
		// try {
		// 	const result = await MapUtils2.executeSearch(this.inputField);
		// 	await this.fillInPlace(result);
		// } catch (e) {
		// 	await this.fillInPlace(pred);
		// }

		this.loading = false;

		this.cdr.markForCheck();
	}

	onAddress() {
		super.onAddress();
		new Promise(async () => {
			if (this.address && this.selectedType === PreorderType.DELIVERY) {
				await this.loadVenues(this.address);
			} else {
				await this.resetMap();
				// this.resetSearch();
			}
		});
	}

	async checkAddressDelivery(venue: Venue, address: Address) {
		if (this.address.number && this.address.city && this.address.street) {
			try {
				const addressValidationResult = ValidationUtils.validateAddress(address, true);
				if (addressValidationResult) {
					return false;
				}
				if (venue.deliveryByAreas && venue?.deliveryAreas?.length > 0) {
					return true;
				}
				const delivers =
					(venue.deliveryByRadius &&
						calculateGeoDistance(address.lat, address.lng, venue.location.coordinates[1], venue.location.coordinates[0]) <=
							venue.deliveryRadius) ||
					(venue.isPostalDelivery && venue.deliveryPostalCodes.indexOf(address.postalCode));

				if (!delivers) {
					this.snackbarCtrl.open(this.translate.instant('errors.street_delivery'), null, {
						duration: 2000,
					});
					return false;
				}

				return true;
			} catch (e) {
				return false;
			}
		} else {
			return false;
		}
	}
	private async scrollToVenue(index: number) {
		try {
			const children = this.venueList.el.children[index] ?? this.venueList.el.children[0]?.children[index];
			await this.venueList.scrollToPoint(0, (children?.offsetTop ?? 0) - 250, 1000);
		} catch (e) {
			console.error(e);
		}
	}
	changeType(item: PreorderType) {
		this.showErrorSelectOrderType = false;
		this.selectedType = item;
		if (item === PreorderType.FOOD_SPOT) {
			if (!this.repository.customer.dndCompany) {
				this.askFoodSpotCode();
			}
		}

		if (item == PreorderType.DELIVERY) {
			this.showDialogForAddress();
		}
	}
	private async showDialogForAddress() {
		const result = await CheckDeliveryRadiusModalComponent.show(this.modalCtrl, this.address);

		if (result?.address) {
			this.repository.address.emit(result.address);
		}
	}

	async askFoodSpotCode() {
		this.loading = true;
		try {
			const result = await EnterFoodspotCodeModalComponent.show(this.modalCtrl);
			if (result && result?.openingHours?.length > 0) {
				result.openingHours = TimeUtils.sanitizeHours(result.openingHours);
				this.repository.companyCode$.next(result);
				if (result.venue) {
					const venue = await this.repository.getVenue(result.venue);
					await this.selectVenue(venue, PreorderType.FOOD_SPOT);
					await sleep(200);
					this.selectedType = null;
				}
			} else {
				this.selectedType = null;
			}
			this.loading = false;
		} catch (e) {
			this.loading = false;
		}
	}
	private setupVenueListKeyboardNavigation() {
		// Wait for the component to be fully rendered
		setTimeout(() => {
			// Get the venue list element
			const venueListElement = document.getElementById('venue-list');
			if (!venueListElement) return;

			// Add a container-level keyboard event listener
			venueListElement.addEventListener('keydown', (event: KeyboardEvent) => {
				// Handle keyboard navigation only for specific keys
				if (!['ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
					return;
				}

				// Get all venue items
				const venueItems = Array.from(document.querySelectorAll('.bsg-venue-item')) as HTMLElement[];

				if (venueItems.length === 0) return;

				// Get the currently focused venue
				const focusedElement = document.activeElement as HTMLElement;
				const currentIndex = venueItems.indexOf(focusedElement);

				// Handle different key presses
				switch (event.key) {
					case 'ArrowUp':
						event.preventDefault();
						// Navigate to previous venue or wrap to the last
						this.focusVenueItem(venueItems, currentIndex > 0 ? currentIndex - 1 : venueItems.length - 1);
						break;

					case 'ArrowDown':
						event.preventDefault();
						// Navigate to next venue or wrap to the first
						this.focusVenueItem(venueItems, currentIndex < venueItems.length - 1 ? currentIndex + 1 : 0);
						break;

					case 'Home':
						event.preventDefault();
						// Navigate to the first venue
						this.focusVenueItem(venueItems, 0);
						break;

					case 'End':
						event.preventDefault();
						// Navigate to the last venue
						this.focusVenueItem(venueItems, venueItems.length - 1);
						break;

					case 'Enter':
					case ' ':
						// Select the venue when Enter or Space is pressed
						// Only if the focused element is a venue item
						if (currentIndex >= 0) {
							event.preventDefault();
							// Get the venue data and call selectVenue
							const venue = this.relevantVenues[currentIndex];
							if (venue) {
								this.selectVenue(venue, this.selectedType);
							}
						}
						break;
				}
			});

			// Make the first venue focusable on initial load
			this.makeFirstVenueFocusable();
		}, 500); // Wait for the view to be fully rendered
	}
	/**
	 * Focus a specific venue item and scroll it into view
	 */
	private focusVenueItem(venueItems: HTMLElement[], index: number) {
		if (index >= 0 && index < venueItems.length) {
			// Focus the venue item
			venueItems[index].focus();

			// Scroll the venue into view
			this.scrollToVenue(index);

			// Announce for screen readers (optional)
			this.announceForScreenReader(`Focused venue: ${this.relevantVenues[index].name}`);
		}
	}

	/**
	 * Scroll to make a venue visible in the viewport
	 */

	/**
	 * Make the first venue focusable on initial load
	 */
	private makeFirstVenueFocusable() {
		const firstVenue = document.querySelector('.bsg-venue-item') as HTMLElement;
		if (firstVenue) {
			firstVenue.setAttribute('tabindex', '0');

			// Make other venues not focusable via tab
			// They will be focusable via arrow keys
			const otherVenues = document.querySelectorAll('.bsg-venue-item:not(:first-child)');
			otherVenues.forEach(venue => {
				(venue as HTMLElement).setAttribute('tabindex', '-1');
			});
		}
	}
	/**
	 * Screen reader announcement helper
	 */
	private announceForScreenReader(message: string) {
		// Create a live region if it doesn't exist
		let announcer = document.getElementById('venue-announcer');

		if (!announcer) {
			announcer = document.createElement('div');
			announcer.id = 'venue-announcer';
			announcer.setAttribute('aria-live', 'polite');
			announcer.style.position = 'absolute';
			announcer.style.width = '1px';
			announcer.style.height = '1px';
			announcer.style.padding = '0';
			announcer.style.margin = '-1px';
			announcer.style.overflow = 'hidden';
			announcer.style.clip = 'rect(0, 0, 0, 0)';
			announcer.style.whiteSpace = 'nowrap';
			announcer.style.border = '0';
			document.body.appendChild(announcer);
		}

		// Set the message
		announcer.textContent = message;

		// Clear after a delay
		setTimeout(() => {
			announcer.textContent = '';
		}, 1000);
	}
	/**
	 * Your existing venue selection method
	 */
}
